import React from 'react';

import { Modal } from '@shared/ui/Modal/Modal';

import { twMerge } from '@utils/twMerge';
import { copyToClipboard } from '@utils/copyToClipboard';
import { TicketCommentModalProps } from './types';
import {
  DEFAULT_JSON_COLLECTION_ITEMS,
  DEFAULT_JSON_RENDER_DEPTH,
  JSON_INDENT_PX,
} from './constants';

type JsonPayloadModalProps = Pick<
  TicketCommentModalProps,
  'open' | 'onCancel'
> & {
  title: string;
  payload?: unknown;
  maxRenderDepth?: number;
  maxCollectionItems?: number;
};

type NormalizedJsonPayload = {
  parsed: unknown;
  formatted: string;
};

const getSerializationFallback = (payload: unknown): string => {
  if (payload instanceof Error) {
    return payload.message;
  }

  return 'Unable to serialize JSON payload.';
};

const normalizeJsonPayload = (payload: unknown): NormalizedJsonPayload => {
  if (payload === undefined || payload === null) {
    return {
      parsed: payload,
      formatted: '',
    };
  }

  if (typeof payload === 'string') {
    try {
      const parsedPayload = JSON.parse(payload);
      return {
        parsed: parsedPayload,
        formatted: JSON.stringify(parsedPayload, null, 2),
      };
    } catch {
      return {
        parsed: payload,
        formatted: payload,
      };
    }
  }

  try {
    return {
      parsed: payload,
      formatted: JSON.stringify(payload, null, 2),
    };
  } catch {
    const fallback = getSerializationFallback(payload);
    return {
      parsed: fallback,
      formatted: fallback,
    };
  }
};

const tokenClassMap = {
  key: 'text-dodgerBlue',
  string: 'text-vividTangelo',
  number: 'text-pastelBlue',
  boolean: 'text-yellowGreen',
  null: 'text-dimGray italic',
  punctuation: 'text-spanishGray',
} as const;

const TruncatedMarker = () => (
  <span className={tokenClassMap.punctuation}>… truncated</span>
);

type JsonValueProps = {
  value: unknown;
  maxRenderDepth: number;
  maxCollectionItems: number;
  level?: number;
};

const JsonValue: React.FC<JsonValueProps> = ({
  value,
  maxRenderDepth,
  maxCollectionItems,
  level = 0,
}) => {
  if (level >= maxRenderDepth) {
    return <TruncatedMarker />;
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return (
        <>
          <span className={tokenClassMap.punctuation}>[</span>
          <span className={tokenClassMap.punctuation}>]</span>
        </>
      );
    }

    return (
      <>
        <span className={tokenClassMap.punctuation}>[</span>
        <div style={{ paddingLeft: JSON_INDENT_PX }}>
          {value.slice(0, maxCollectionItems).map((item, index) => (
            <div key={`${level}-${index}`}>
              <JsonValue
                value={item}
                maxRenderDepth={maxRenderDepth}
                maxCollectionItems={maxCollectionItems}
                level={level + 1}
              />
              {index < Math.min(value.length, maxCollectionItems) - 1 && (
                <span className={tokenClassMap.punctuation}>,</span>
              )}
            </div>
          ))}
          {value.length > maxCollectionItems && (
            <div>
              <TruncatedMarker />
            </div>
          )}
        </div>
        <span className={tokenClassMap.punctuation}>]</span>
      </>
    );
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    const visibleEntries = entries.slice(0, maxCollectionItems);

    if (!entries.length) {
      return (
        <>
          <span className={tokenClassMap.punctuation}>{'{'}</span>
          <span className={tokenClassMap.punctuation}>{'}'}</span>
        </>
      );
    }

    return (
      <>
        <span className={tokenClassMap.punctuation}>{'{'}</span>
        <div style={{ paddingLeft: JSON_INDENT_PX }}>
          {visibleEntries.map(([key, entryValue], index) => (
            <div key={`${level}-${key}`}>
              <span className={tokenClassMap.key}>"{key}"</span>
              <span className={tokenClassMap.punctuation}>: </span>
              <JsonValue
                value={entryValue}
                maxRenderDepth={maxRenderDepth}
                maxCollectionItems={maxCollectionItems}
                level={level + 1}
              />
              {index < visibleEntries.length - 1 && (
                <span className={tokenClassMap.punctuation}>,</span>
              )}
            </div>
          ))}
          {entries.length > maxCollectionItems && (
            <div>
              <TruncatedMarker />
            </div>
          )}
        </div>
        <span className={tokenClassMap.punctuation}>{'}'}</span>
      </>
    );
  }

  if (typeof value === 'string') {
    return <span className={tokenClassMap.string}>"{value}"</span>;
  }

  if (typeof value === 'number') {
    return <span className={tokenClassMap.number}>{String(value)}</span>;
  }

  if (typeof value === 'boolean') {
    return <span className={tokenClassMap.boolean}>{String(value)}</span>;
  }

  if (value === null) {
    return <span className={tokenClassMap.null}>null</span>;
  }

  return <span className={tokenClassMap.string}>Unsupported value</span>;
};

export const JsonPayloadModal: React.FC<JsonPayloadModalProps> = ({
  open,
  onCancel,
  title,
  payload,
  maxRenderDepth = DEFAULT_JSON_RENDER_DEPTH,
  maxCollectionItems = DEFAULT_JSON_COLLECTION_ITEMS,
}) => {
  const normalizedPayload = React.useMemo(
    () => normalizeJsonPayload(payload),
    [payload]
  );
  const { formatted, parsed } = normalizedPayload;

  const hasPayload = formatted.trim().length > 0;

  const handleCopy = React.useCallback(() => {
    if (!hasPayload) return;
    copyToClipboard(formatted);
  }, [formatted, hasPayload]);

  return (
    <Modal
      width={760}
      open={open}
      onCancel={onCancel}
      onOk={handleCopy}
      okText='Copy content'
      cancelText='Close'
      title={title}
      classNames={{
        footer: 'flex items-center justify-between mt-[16px]',
      }}
      okProps={{
        type: 'primary',
        disabled: !hasPayload,
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[15px] !border-0',
          !hasPayload
            ? 'opacity-50 cursor-not-allowed !bg-vividTangelo !text-white'
            : '!bg-vividTangelo hover:!bg-dodgerBlue !text-white hover:!text-white'
        ),
      }}
      cancelProps={{
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[15px]',
          'bg-dimGray hover:bg-waterLightHover50',
          '!text-black hover:!text-white'
        ),
      }}
    >
      <div className='py-[8px]'>
        <div className='max-h-[50vh] overflow-auto rounded-[16px] border border-waterLight bg-[#F6F9FF] p-4'>
          {hasPayload ? (
            <pre className='m-0 whitespace-pre-wrap break-words font-mono text-[13px] leading-6'>
              <JsonValue
                value={parsed}
                maxRenderDepth={maxRenderDepth}
                maxCollectionItems={maxCollectionItems}
              />
            </pre>
          ) : (
            <pre className='m-0 whitespace-pre-wrap break-words font-mono text-[13px] leading-6 text-spanishGray'>
              No JSON payload available.
            </pre>
          )}
        </div>
      </div>
    </Modal>
  );
};
